<template>
  <div
    class="floating-projects"
    :class="{ '-animating': isAnimating }"
    data-floating-container
  >
    <!-- 0: title-heading -->
    <div
      v-if="title"
      data-floating-object
      data-floating-heading
      data-transition="down"
      class="object title-heading"
    >
      <h1 class="title" data-title-heading>
        {{ title }}
      </h1>
    </div>

    <!-- 1: first project -->
    <div
      v-if="firstProject"
      data-floating-object
      data-floating-link
      data-transition="up"
      class="object"
      data-index="0"
    >
      <NuxtLink class="link" :to="`/projekte/${firstProject.data.slug}`">
        <DatoAsset
          v-if="firstProject.data.image"
          fade-in="none"
          class="asset -hover-enabled"
          :asset="firstProject.data.image"
          :asset-vs="firstProject.data.smallImage ?? undefined"
          :hide-copyright="true"
        />
        <div v-else class="asset placeholder" />
        <p class="label">
          <b class="title">{{ firstProject.data.shortTitle }}</b>
          <span class="divider" />
          <span class="service">{{ firstProject.data.service?.title }}</span>
        </p>
      </NuxtLink>
    </div>

    <!-- 2: title-text -->
    <div
      v-if="cta"
      data-floating-object
      data-floating-text
      data-transition="down"
      class="object title-text"
    >
      <p class="text" data-title-text>
        {{ cta }}
      </p>
    </div>

    <!-- 3+: remaining projects -->
    <div
      v-for="(p, pIndex) in restProjects"
      :key="p.index"
      data-floating-object
      data-floating-link
      data-transition="up"
      class="object"
      :data-index="pIndex + 1"
    >
      <NuxtLink class="link" :to="`/projekte/${p.data.slug}`">
        <DatoAsset
          v-if="p.data.image"
          fade-in="none"
          class="asset -hover-enabled"
          :asset="p.data.image"
          :asset-vs="p.data.smallImage ?? undefined"
          :hide-copyright="true"
        />
        <div v-else class="asset placeholder" />
        <p class="label">
          <b class="title">{{ p.data.shortTitle }}</b>
          <span class="divider" />
          <span class="service">{{ p.data.service?.title }}</span>
        </p>
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import gsap from 'gsap';
import CustomEase from 'gsap/CustomEase';
import infiniteFloatingBoxes from '~/animation/infiniteFloatingBoxes';
import type { ProjectFragment } from '#gql';
import DatoAsset from '~/dato/DatoAsset.vue';
import type { DatoAssetDto } from '~/dato/dato';

gsap.registerPlugin(CustomEase);

const props = defineProps<{
  data: ProjectFragment[];
  title: string;
  cta: string;
}>();

const transformedProjects = computed(() => {
  return props.data.map((p) => {
    const image: DatoAssetDto | undefined = p.image
      ? {
          ...p.image,
          alt: p.image.responsiveImage?.alt ?? '',
          responsiveImage: p.image.responsiveImage ?? undefined,
          copyright: p.image.copyright ?? ''
        }
      : undefined;

    const smallImage: DatoAssetDto | undefined = p.smallImage
      ? {
          ...p.smallImage,
          alt: p.smallImage.responsiveImage?.alt ?? '',
          responsiveImage: p.smallImage.responsiveImage ?? undefined,
          copyright: p.smallImage.copyright ?? ''
        }
      : undefined;

    return {
      ...p,
      data: {
        ...p,
        image,
        smallImage
      }
    };
  });
});

const isAnimating = ref<boolean>(false);
const reorderedProjects = sortProjects();
const firstProject = reorderedProjects[0] ?? null;
const restProjects = reorderedProjects.slice(1);
let floatingBoxes: ReturnType<typeof infiniteFloatingBoxes> | null = null;

function sortProjects() {
  const order = [7, 6, 5, 2, 9, 8, 1, 0, 3, 4];

  const list = transformedProjects.value.map((data, index) => {
    return {
      index,
      data: data.data
    };
  });

  if (list.length === order.length) {
    return list.sort((a, b) => {
      return order.indexOf(a.index) - order.indexOf(b.index);
    });
  }
  return list;
}

onMounted(() => {
  const objects = Array.from(
    document.querySelectorAll('[data-floating-object]')
  ) as HTMLElement[];

  floatingBoxes = infiniteFloatingBoxes();
  floatingBoxes.init(objects);
  isAnimating.value = true;
  addTitleAnimation();
});

function addTitleAnimation() {
  const title = document.querySelector('[data-title-heading]');
  const text = document.querySelector('[data-title-text]');
  const links = Array.from(
    document.querySelectorAll('[data-floating-link]')
  ) as HTMLElement[];
  const timeline = gsap.timeline();
  CustomEase.create('custom', '0.75, 0, 0.25, 1');

  if (title && text) {
    timeline.fromTo(
      [text, title],
      {
        y: 100,
        clipPath: 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)'
      },
      {
        y: 0,
        clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
        duration: 1.2,
        ease: 'custom',
        stagger: 0.2
      }
    );
    timeline.fromTo(
      [text, title, ...links],
      {
        opacity: 0
      },
      {
        opacity: 1,
        delay: 0.4,
        duration: 0.8,
        ease: 'ease-in-out',
        stagger: 0.2
      },
      '<'
    );
  }
}
</script>

<style scoped lang="scss">
.floating-projects {
  width: 100vw;
  height: 100svh;
  overflow: clip;

  &.-animating {
    visibility: visible;
  }

  &:not(.-animating) {
    visibility: hidden;
  }

  > .object {
    position: absolute;
    display: block;

    @media (--vs) {
      overflow: clip;
    }

    @media (hover: hover) {
      &:hover {
        > .link {
          > .label {
            transform: translateY(5px);
          }
        }
      }
    }

    > .link {
      text-decoration: none;

      > .asset {
        --background-color: var(--color-off-white) !important;
        width: 100%;
        height: calc(100% - 2rem);

        > :deep(.image) {
          object-fit: cover;
          width: 100%;
          height: 100%;
        }

        @media (orientation: portrait) {
          > :deep(.image) {
            max-height: 45vh;
          }
        }
      }

      > .placeholder {
        width: 100%;
        height: calc(100% - 2rem);
        background-color: var(--color-grey);
      }

      > .label {
        display: flex;
        gap: 0.5rem;
        align-items: center;
        padding: 0.5rem 0 0 0;
        transition: transform 500ms cubic-bezier(0.4, 0, 0.2, 1);

        > .title {
          @include text-xs(700);

          overflow-x: clip;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 100%;
        }

        > .divider {
          width: 1.5rem;
          height: 1px;
          background-color: var(--color-black);
          display: inline-block;
        }

        > .service {
          @include text-xs(300);

          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }

  > .title-text {
    @media (--vl) {
      width: max(300px, 18vw);
    }

    > .text {
      display: flex;
      align-items: center;
      z-index: 2;
      pointer-events: none;

      @include text-xl(700);
    }
  }

  > .title-heading {
    @media (--vl) {
      width: max(420px, 38vw);
    }

    > .title {
      z-index: 2;
      pointer-events: none;

      @media (--vs) {
        @include text-3xl(700);
      }

      @media (--vl) {
        @include text-4xl(700);
      }
    }
  }
}
</style>
